/**
 * Confirmação da exclusão de um pagamento.
 *
 * Pede confirmação porque a consequência não é óbvia olhando a linha: se a cobrança já sinalizou
 * faturas neste pagamento, esse trabalho SE PERDE — as faturas voltam a não estar marcadas. Quem
 * exclui é do financeiro e não vê a fila do outro time, então o aviso é explícito e pede que
 * avisem.
 *
 * Pagamento já identificado não chega aqui (o menu desabilita), mas o servidor recusa de todo
 * jeito: ali as faturas estão pagas e o settlement já movimentou os títulos.
 */
import { useState } from 'react';
import { FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { PaymentRow, deletePayment } from '../services/billingApi';
import { formatBRL } from '../../../../utils/currencyUtils';

interface Props {
    payment: PaymentRow;
    onClose: () => void;
    onDeleted: () => void;
}

const DeletePaymentDialog: React.FC<Props> = ({ payment, onClose, onDeleted }) => {
    const [excluindo, setExcluindo] = useState(false);
    const marcadas = payment.linkedInvoicesCount || 0;

    const confirmar = async () => {
        setExcluindo(true);
        try {
            await deletePayment(payment.paymentId);
            toast.success('Pagamento excluído.');
            onDeleted();
            onClose();
        } catch (e: any) {
            toast.error(`Não foi possível excluir: ${e.message}`);
        } finally {
            setExcluindo(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg">
                <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-600">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                        <FaExclamationTriangle className="text-amber-500" /> Excluir pagamento
                    </h3>
                    <button onClick={onClose} aria-label="Fechar"
                        className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                </div>

                <div className="px-6 py-4 text-sm space-y-3">
                    <p>
                        Excluir o pagamento de{' '}
                        <strong>{formatBRL(payment.amount || 0)}</strong>
                        {payment.payer?.name && <> de <strong>{payment.payer.name}</strong></>}?
                    </p>

                    {/* o aviso só aparece quando há o que perder — alarme em pagamento sem
                        marcação treina o usuário a ignorar o texto */}
                    {marcadas > 0 && (
                        <div className="px-3 py-2 rounded bg-amber-50 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200">
                            <p className="font-medium">
                                {marcadas === 1
                                    ? 'Há 1 fatura sinalizada neste pagamento.'
                                    : `Há ${marcadas} faturas sinalizadas neste pagamento.`}
                            </p>
                            <p className="mt-1">
                                Elas <strong>perderão a sinalização</strong> e voltarão a ficar sem
                                pagamento vinculado. As faturas continuam abertas — o que se perde é
                                o trabalho de identificação já feito.
                            </p>
                            <p className="mt-1">
                                Se for o caso, <strong>avise o time de cobrança</strong>: eles
                                precisarão sinalizar novamente.
                            </p>
                        </div>
                    )}

                    <p className="text-gray-500">Esta ação fica registrada no log de cobrança.</p>
                </div>

                <div className="px-6 py-4 border-t dark:border-slate-600 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                        Cancelar
                    </button>
                    <button onClick={confirmar} disabled={excluindo}
                        className="px-6 py-2 bg-red-600 text-white rounded-lg disabled:opacity-50 hover:bg-red-700">
                        {excluindo ? 'Excluindo...' : 'Excluir pagamento'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DeletePaymentDialog;
